/**
 * @file University.store.js
 * @description MobX store for USABG University state management
 *
 * This store manages all state for the University module including:
 * - Sessions: Training session data and CRUD operations
 * - Locations: Training location data and CRUD operations
 * - Enrollments: User enrollment status and lifecycle management
 * - Payments: Stripe checkout integration and verification
 *
 * State Flow:
 * 1. init() is called on page load
 * 2. Fetches sessions, user's enrollment, and deposit amount
 * 3. Components observe store properties and call actions
 *
 * @see /docs/modules/UsabgUniversity/README.md for full documentation
 */
import { makeAutoObservable, runInAction } from 'mobx'
import UniversitySessionService from './../../../shared/services/UniversitySession.service'
import UniversityLocationService from './../../../shared/services/UniversityLocation.service'
import UsersUniversitySessionService from './../../../shared/services/UsersUniversitySession.service'
import UniversityPaymentService from './../../../shared/services/UniversityPayment.service'
import UserService from './../../../shared/services/User.service'
import UserProfileService from './../../../shared/services/UserProfile.service'
import AdminApi from './../../../api/admin-api/admin-api'
import Papa from 'papaparse'

const DEFAULT_DEPOSIT_AMOUNT = 200

/**
 * Number of days a session stays in the Admin Panel's active list after it
 * ends. Admins still need to reconcile enrollees, refunds and attendance for a
 * class that just wrapped, so a session only moves to "past" after this window.
 */
const ADMIN_RECENT_WINDOW_DAYS = 7

/**
 * Today as a YYYY-MM-DD string in the viewer's local timezone, optionally
 * offset by a number of days. Session dates are date-only values, so comparing
 * against a UTC "today" would roll the boundary over a half day early for
 * US-based admins.
 */
const localDateString = (offsetDays = 0) => {
  const date = new Date()
  if (offsetDays) date.setDate(date.getDate() + offsetDays)
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

/**
 * Reduce a date value to its YYYY-MM-DD prefix so string comparison stays
 * correct whether the API returns "2026-08-03" or a full ISO timestamp.
 */
const dateOnly = value => (value ? `${value}`.slice(0, 10) : '')

/**
 * The date a session is over, falling back to start_date when end_date is null.
 */
const sessionEndDate = session => dateOnly(session?.end_date) || dateOnly(session?.start_date)

/**
 * Whether a session has already ended, for admin-side visual treatment.
 * Exported as a plain function rather than a store method: `makeAutoObservable`
 * would wrap a class method as an action, and actions run untracked, so reading
 * it from a render would not register an observable dependency.
 */
export const isSessionEnded = session => {
  const end = sessionEndDate(session)
  return !!end && end < localDateString()
}

/**
 * UniversityStore - MobX store for USABG University module
 *
 * @class
 * @property {Array} allSessions - Every non-deleted session, unfiltered by date
 * @property {Array} locations - List of training locations
 * @property {Object|null} myEnrollment - Current user's enrollment (if any)
 * @property {Object} sessionEnrollments - Enrollments keyed by session ID
 * @property {number} depositAmount - Deposit amount from system settings
 * @property {Array} downlineAgents - User's downline for invitations
 * @property {number|null} expandedSessionId - Currently expanded session in admin
 * @property {boolean} showPastSessions - Admin Panel past/closed session toggle
 * @property {Object} isLoading - Loading states for various operations
 *
 * Sessions are fetched once into `allSessions` and narrowed by two computeds:
 * `sessions` (agent-facing: future start dates only) and `adminSessions`
 * (management: recently-ended sessions included, all sessions when the
 * past/closed toggle is on).
 */
class UniversityStore {
  // ==================== Observable State ====================
  allSessions = []
  locations = []
  myEnrollment = null
  sessionEnrollments = {} // { sessionId: [enrollments] }
  depositAmount = DEFAULT_DEPOSIT_AMOUNT
  downlineAgents = []
  expandedSessionId = null // Currently expanded session for enrollee management
  showPastSessions = false // Admin Panel: include ended sessions in the table

  isLoading = {
    sessions: false,
    locations: false,
    enrollment: false,
    enrolling: false,
    inviting: false,
    downline: false,
    sessionEnrollments: false,
  }

  error = null

  constructor() {
    makeAutoObservable(this)
  }

  /**
   * Initialize the store - fetch all required data
   */
  init = async () => {
    await Promise.all([this.fetchSessions(), this.fetchMyEnrollment(), this.fetchDepositAmount()])
  }

  /**
   * Fetch all non-deleted sessions. Date filtering happens in the `sessions`
   * and `adminSessions` computeds so agent-facing and admin views can differ.
   */
  fetchSessions = async () => {
    this.isLoading.sessions = true
    this.error = null

    try {
      const response = await UniversitySessionService.search({
        pagination: false,
        expand: ['location', 'host', 'enrolled_count'],
      })

      runInAction(() => {
        // REST API client returns {models: [...]} or array directly
        const models = response?.models || (Array.isArray(response) ? response : [])
        this.allSessions = models
          .filter(s => !s.deleted_at)
          .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
      })
    } catch (ex) {
      runInAction(() => {
        this.error = 'Failed to fetch sessions'
        console.error('Error fetching sessions:', ex)
      })
    } finally {
      runInAction(() => {
        this.isLoading.sessions = false
      })
    }
  }

  /**
   * Fetch all locations (for admin panel)
   */
  fetchLocations = async () => {
    this.isLoading.locations = true

    try {
      const response = await UniversityLocationService.search({
        pagination: false,
        expand: ['owner'],
      })

      runInAction(() => {
        // REST API client returns {models: [...]} or array directly
        this.locations = response?.models || (Array.isArray(response) ? response : [])
      })
    } catch (ex) {
      runInAction(() => {
        console.error('Error fetching locations:', ex)
      })
    } finally {
      runInAction(() => {
        this.isLoading.locations = false
      })
    }
  }

  /**
   * Search users for owner selector
   * Uses the /userSearch endpoint which supports OR logic for first, last, full name, or email
   */
  searchUsers = async query => {
    try {
      const response = await AdminApi.getUsersFromSearchTerm(query)
      return response?.data?.data || []
    } catch (ex) {
      console.error('Error searching users:', ex)
      return []
    }
  }

  /**
   * Fetch current user's enrollment in upcoming sessions
   */
  fetchMyEnrollment = async () => {
    this.isLoading.enrollment = true

    try {
      const response = await UsersUniversitySessionService.search({}, { endpoint: '/my-enrollment' })

      runInAction(() => {
        // REST API client returns {models: [...]} or array directly
        const enrollments = response?.models || (Array.isArray(response) ? response : [])
        // Get the most recent/relevant enrollment
        this.myEnrollment = enrollments.length > 0 ? enrollments[0] : null
      })
    } catch (ex) {
      runInAction(() => {
        console.error('Error fetching my enrollment:', ex)
      })
    } finally {
      runInAction(() => {
        this.isLoading.enrollment = false
      })
    }
  }

  /**
   * Fetch deposit amount from system settings
   */
  fetchDepositAmount = async () => {
    try {
      const response = await UniversityPaymentService.getDepositAmount()
      runInAction(() => {
        this.depositAmount = response?.data?.amount || DEFAULT_DEPOSIT_AMOUNT
      })
    } catch (ex) {
      console.error('Error fetching deposit amount:', ex)
    }
  }

  /**
   * Fetch enrollments for a specific session (admin use)
   */
  fetchSessionEnrollments = async sessionId => {
    this.isLoading.sessionEnrollments = true

    try {
      const response = await UsersUniversitySessionService.search({}, { endpoint: `/by-session/${sessionId}` })

      runInAction(() => {
        // API returns payload array directly
        this.sessionEnrollments[sessionId] =
          response?.payload || response?.models || (Array.isArray(response) ? response : [])
      })
    } catch (ex) {
      console.error('Error fetching session enrollments:', ex)
    } finally {
      runInAction(() => {
        this.isLoading.sessionEnrollments = false
      })
    }
  }

  /**
   * Toggle session expansion for enrollee management
   */
  toggleSessionExpand = sessionId => {
    if (this.expandedSessionId === sessionId) {
      this.expandedSessionId = null
    } else {
      this.expandedSessionId = sessionId
      this.fetchSessionEnrollments(sessionId)
    }
  }

  /**
   * Fetch downline agents for the invitor component
   */
  fetchDownlineAgents = async () => {
    this.isLoading.downline = true

    try {
      const userId = UserProfileService.getUserId()
      const response = await UserService.getDownline(userId)

      runInAction(() => {
        this.downlineAgents = response?.data?.payload || []
      })
    } catch (ex) {
      runInAction(() => {
        console.error('Error fetching downline:', ex)
      })
    } finally {
      runInAction(() => {
        this.isLoading.downline = false
      })
    }
  }

  /**
   * Self-enroll in a session
   */
  enrollInSession = async sessionId => {
    this.isLoading.enrolling = true
    this.error = null

    try {
      const response = await UsersUniversitySessionService.store(
        {
          session_id: sessionId,
        },
        { endpoint: '/enroll' }
      )

      runInAction(() => {
        if (response?.data?.model) {
          this.myEnrollment = response.data.model
        }
      })

      // Refresh sessions to update enrollment count
      await this.fetchSessions()

      return { success: true, enrollment: response?.data?.model }
    } catch (ex) {
      runInAction(() => {
        this.error = ex?.response?.data?.message || 'Failed to enroll'
      })
      return { success: false, error: this.error }
    } finally {
      runInAction(() => {
        this.isLoading.enrolling = false
      })
    }
  }

  /**
   * Accept an invitation
   */
  acceptInvitation = async enrollmentId => {
    this.isLoading.enrolling = true

    try {
      await UsersUniversitySessionService.store({}, { endpoint: `/${enrollmentId}/accept` })

      // Re-fetch enrollment to get updated data with session details
      await this.fetchMyEnrollment()

      return { success: true }
    } catch (ex) {
      return {
        success: false,
        error: ex?.response?.data?.message || 'Failed to accept invitation',
      }
    } finally {
      runInAction(() => {
        this.isLoading.enrolling = false
      })
    }
  }

  /**
   * Invite an agent to a session
   */
  inviteAgent = async (userId, sessionId) => {
    this.isLoading.inviting = true

    try {
      const response = await UsersUniversitySessionService.store(
        {
          user_id: userId,
          session_id: sessionId,
        },
        { endpoint: '/invite' }
      )

      return { success: true, enrollment: response?.data?.model }
    } catch (ex) {
      return {
        success: false,
        error: ex?.response?.data?.message || 'Failed to send invitation',
      }
    } finally {
      runInAction(() => {
        this.isLoading.inviting = false
      })
    }
  }

  /**
   * Create Stripe checkout session for deposit payment
   */
  createPaymentCheckout = async enrollmentId => {
    try {
      const response = await UniversityPaymentService.createCheckoutSession(enrollmentId)

      // REST API client returns response.data.payload directly
      if (response?.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = response.checkout_url
        return { success: true }
      }

      return { success: false, error: 'No checkout URL returned' }
    } catch (ex) {
      return {
        success: false,
        error: ex?.response?.data?.message || ex?.message || 'Failed to create checkout',
      }
    }
  }

  /**
   * Verify payment status by checking Stripe directly
   * Used as fallback when webhooks don't arrive (e.g., local development)
   */
  verifyPayment = async (enrollmentId, sessionId) => {
    try {
      const response = await UniversityPaymentService.verifyPayment(enrollmentId, sessionId)

      if (response?.verified) {
        // Update local enrollment data
        if (response.enrollment) {
          runInAction(() => {
            this.myEnrollment = response.enrollment
          })
        } else {
          // Refresh enrollment data
          await this.fetchMyEnrollment()
        }
        return { success: true, verified: true }
      }

      return { success: true, verified: false, message: response?.message }
    } catch (ex) {
      return {
        success: false,
        error: ex?.response?.data?.message || ex?.message || 'Failed to verify payment',
      }
    }
  }

  // ==================== Admin Actions ====================

  /**
   * Create a new location
   */
  createLocation = async data => {
    try {
      const response = await UniversityLocationService.store(data)
      await this.fetchLocations()
      return { success: true, location: response?.data?.model }
    } catch (ex) {
      return {
        success: false,
        error: ex?.response?.data?.message || 'Failed to create location',
      }
    }
  }

  /**
   * Update a location
   */
  updateLocation = async (id, data) => {
    try {
      const response = await UniversityLocationService.update(id, data)
      await this.fetchLocations()
      return { success: true, location: response?.data?.model }
    } catch (ex) {
      return {
        success: false,
        error: ex?.response?.data?.message || 'Failed to update location',
      }
    }
  }

  /**
   * Delete a location
   */
  deleteLocation = async id => {
    try {
      await UniversityLocationService.delete(id)
      await this.fetchLocations()
      return { success: true }
    } catch (ex) {
      return {
        success: false,
        error: ex?.response?.data?.message || 'Failed to delete location',
      }
    }
  }

  /**
   * Create a new session
   */
  createSession = async data => {
    try {
      const response = await UniversitySessionService.store(data)
      await this.fetchSessions()
      return { success: true, session: response?.data?.model }
    } catch (ex) {
      return {
        success: false,
        error: ex?.response?.data?.message || 'Failed to create session',
      }
    }
  }

  /**
   * Update a session
   */
  updateSession = async (id, data) => {
    try {
      const response = await UniversitySessionService.update(id, data)
      await this.fetchSessions()
      return { success: true, session: response?.data?.model }
    } catch (ex) {
      return {
        success: false,
        error: ex?.response?.data?.message || 'Failed to update session',
      }
    }
  }

  /**
   * Delete a session (with optional refund)
   */
  deleteSession = async (id, issueRefunds = false) => {
    try {
      await UniversitySessionService.delete(id, { issue_refunds: issueRefunds })
      await this.fetchSessions()
      return { success: true }
    } catch (ex) {
      return {
        success: false,
        error: ex?.response?.data?.message || 'Failed to delete session',
      }
    }
  }

  /**
   * Get enrollment count for a session
   */
  getEnrollmentCount = async sessionId => {
    try {
      const response = await UniversitySessionService.get(sessionId, null, {
        endpoint: `/${sessionId}/enrollment-count`,
      })
      return response?.data || { total: 0, paid: 0, unpaid: 0 }
    } catch (ex) {
      return { total: 0, paid: 0, unpaid: 0 }
    }
  }

  // ==================== Enrollee Management ====================

  /**
   * Check-in an enrollee (initiate)
   */
  initiateEnrollment = async (enrollmentId, sessionId) => {
    try {
      await UsersUniversitySessionService.initiate(enrollmentId)
      await this.fetchSessionEnrollments(sessionId)
      return { success: true }
    } catch (ex) {
      return {
        success: false,
        error: ex?.response?.data?.message || 'Failed to check in',
      }
    }
  }

  /**
   * Mark an enrollee as completed
   */
  completeEnrollment = async (enrollmentId, sessionId) => {
    try {
      await UsersUniversitySessionService.complete(enrollmentId)
      await this.fetchSessionEnrollments(sessionId)
      return { success: true }
    } catch (ex) {
      return {
        success: false,
        error: ex?.response?.data?.message || 'Failed to complete',
      }
    }
  }

  /**
   * Cancel an enrollment with optional refund
   */
  cancelEnrollment = async (enrollmentId, sessionId, issueRefund = false) => {
    try {
      await UsersUniversitySessionService.cancel(enrollmentId, issueRefund)
      await this.fetchSessionEnrollments(sessionId)
      await this.fetchSessions() // Refresh enrollment counts
      return { success: true }
    } catch (ex) {
      return {
        success: false,
        error: ex?.response?.data?.message || 'Failed to cancel',
      }
    }
  }

  /**
   * Change an enrollee's session (reassign to a different session).
   * Refreshes both the source and target session enrollee lists,
   * plus the sessions list to update enrolled_count on both.
   */
  changeEnrollmentSession = async (enrollmentId, currentSessionId, targetSessionId) => {
    try {
      await UsersUniversitySessionService.changeSession(enrollmentId, targetSessionId)
      // Refresh both sessions' enrollee lists and session counts
      await Promise.all([
        this.fetchSessionEnrollments(currentSessionId),
        this.fetchSessionEnrollments(targetSessionId),
        this.fetchSessions(),
      ])
      return { success: true }
    } catch (ex) {
      return {
        success: false,
        error: ex?.response?.data?.message || 'Failed to change session',
      }
    }
  }

  /**
   * Bulk check-in enrollees
   */
  bulkInitiateEnrollments = async (enrollmentIds, sessionId) => {
    try {
      await UsersUniversitySessionService.bulkInitiate(enrollmentIds)
      await this.fetchSessionEnrollments(sessionId)
      return { success: true }
    } catch (ex) {
      return {
        success: false,
        error: ex?.response?.data?.message || 'Bulk check-in failed',
      }
    }
  }

  /**
   * Bulk complete enrollees
   */
  bulkCompleteEnrollments = async (enrollmentIds, sessionId) => {
    try {
      await UsersUniversitySessionService.bulkComplete(enrollmentIds)
      await this.fetchSessionEnrollments(sessionId)
      return { success: true }
    } catch (ex) {
      return {
        success: false,
        error: ex?.response?.data?.message || 'Bulk complete failed',
      }
    }
  }

  // ==================== Export ====================

  /**
   * Export all attendees for a session as CSV.
   * Fetches enriched user data from the export endpoint and generates
   * a client-side CSV download via PapaParse.
   */
  exportSessionAttendees = async session => {
    try {
      const response = await UsersUniversitySessionService.exportBySession(session.id)

      // REST API client unwraps response.data.payload, so response
      // is already { session, enrollments } directly.
      const data = response?.enrollments ? response : response?.payload
      if (!data || !data.enrollments) {
        return { success: false, error: 'No data returned.' }
      }

      if (data.enrollments.length === 0) {
        return { success: false, error: 'No enrollees to export.' }
      }

      const getStatus = e => {
        if (e.completed_at) return 'Completed'
        if (e.initiated_at) return 'Checked In'
        if (e.refunded_at) return 'Refunded'
        if (e.paid_at) return 'Paid'
        if (e.accepted_at) return 'Accepted'
        if (e.invited_at) return 'Invited'
        if (e.requested_at) return 'Enrolled'
        return 'Pending'
      }

      const formatDate = dateStr => {
        if (!dateStr) return ''
        try {
          const d = new Date(dateStr)
          return d.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
          })
        } catch {
          return dateStr
        }
      }

      const rows = data.enrollments.map(e => ({
        'First Name': e.first_name,
        'Last Name': e.last_name,
        Email: e.email,
        Phone: e.phone,
        Address: e.address,
        'Address 2': e.address_2,
        City: e.city,
        State: e.state,
        ZIP: e.zip,
        NPN: e.npn,
        Upline: e.upline,
        'Upline Email': e.upline_email,
        Division: e.division,
        Status: getStatus(e),
        'Enrolled Date': formatDate(e.requested_at),
        'Invited Date': formatDate(e.invited_at),
        'Accepted Date': formatDate(e.accepted_at),
        'Paid Date': formatDate(e.paid_at),
        'Checked In Date': formatDate(e.initiated_at),
        'Completed Date': formatDate(e.completed_at),
        'Refunded Date': formatDate(e.refunded_at),
      }))

      const csv = Papa.unparse(rows)
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')

      const locationName = session.location?.name || 'Unknown'
      const startDate = String(session.start_date).split('T')[0]
      const fileName = `USABG-University-Attendees-${locationName}-${startDate}.csv`.replace(/[^a-zA-Z0-9\-_.]/g, '_')

      link.href = url
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      return { success: true }
    } catch (ex) {
      console.error('Error exporting session attendees:', ex)
      return {
        success: false,
        error: ex?.response?.data?.message || 'Failed to export attendees.',
      }
    }
  }

  // ==================== Computed Values ====================

  get isEnrolled() {
    return !!this.myEnrollment
  }

  get hasPaid() {
    return !!this.myEnrollment?.paid_at
  }

  get enrollmentStatus() {
    if (!this.myEnrollment) return null

    if (this.myEnrollment.completed_at) return 'completed'
    if (this.myEnrollment.initiated_at) return 'in_progress'
    if (this.myEnrollment.paid_at) return 'paid'
    if (this.myEnrollment.accepted_at) return 'accepted'
    if (this.myEnrollment.invited_at) return 'invited'
    if (this.myEnrollment.requested_at) return 'enrolled'

    return 'pending'
  }

  /**
   * Agent-facing sessions: upcoming only. Used by Calendar, Signup, Invitor and
   * the Admin Panel's "move attendee to another session" dropdown, none of
   * which should offer a class that has already started.
   */
  get sessions() {
    const today = localDateString()
    return this.allSessions.filter(s => dateOnly(s.start_date) >= today)
  }

  /**
   * Admin Panel sessions. Keeps in-progress and recently-ended classes in the
   * list so they stay manageable, and returns everything when the past/closed
   * toggle is on. Sessions with no end_date fall back to their start_date.
   */
  get adminSessions() {
    if (this.showPastSessions) return this.allSessions

    const cutoff = localDateString(-ADMIN_RECENT_WINDOW_DAYS)
    return this.allSessions.filter(s => sessionEndDate(s) >= cutoff)
  }

  /**
   * Count of sessions hidden by the past/closed filter, for the toggle label.
   */
  get pastSessionCount() {
    const cutoff = localDateString(-ADMIN_RECENT_WINDOW_DAYS)
    return this.allSessions.filter(s => sessionEndDate(s) < cutoff).length
  }

  toggleShowPastSessions = () => {
    this.showPastSessions = !this.showPastSessions
  }

  get availableSessions() {
    // Sessions that user can enroll in (not already enrolled)
    if (!this.myEnrollment) return this.sessions

    return this.sessions.filter(s => s.id !== this.myEnrollment.session_id)
  }
}

const store = new UniversityStore()
export default store
